import React, { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { Button, Modal, Pressable, ColorInput } from '../../../../shared/ui';

import forest from '../../../../assets/images/dpdg/forest.jpg';

export interface IVideoList {
  id: number;
  sort: number | null;
  title: string;
  description: string | null;
  thumbnail: string;
  video: string;
}

export interface IVideoOptionsResponse {
  data: IVideoList[];
}

export interface IBackgroundImageList {
  id: number;
  sort: number | null;
  title: string;
  description: string | null;
  image: string;
}

export interface IBackgroundImagesResponse {
  data: IBackgroundImageList[];
}

export type BackgroundSelection =
  | { type: 'none'; color: string }
  | { type: 'video'; item: IVideoList }
  | { type: 'image'; item: IBackgroundImageList };

interface IBackgroundModalProps {
  isShow: boolean;
  onClose: () => void;
  onApply: (selection: BackgroundSelection) => void;
  initialType: 'none' | 'video' | 'image';
  initialColor: string;
  initialValue: string;
  backgroundOptions: IVideoOptionsResponse;
  backgroundImages: IBackgroundImagesResponse;
  isVideosLoading?: boolean;
  isImagesLoading?: boolean;
}

const DIRECTUS_DOMAIN = (
  (import.meta.env.VITE_REACT_APP_DIRECTUS_DOMAIN as string | undefined) ?? 'https://d-t1.zingizi.pro'
).replace(/\/$/, '');

const makeAssetUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;

  return `${DIRECTUS_DOMAIN}/assets/${path}`;
};

const BackgroundSettingsModal: FC<IBackgroundModalProps> = ({
  isShow,
  onClose,
  onApply,
  initialType,
  initialColor,
  initialValue,
  backgroundOptions,
  backgroundImages,
  isVideosLoading,
  isImagesLoading,
}) => {
  const videoTabEnabled = false; // TODO(analytics-team): вернуть вкладку «Видео», когда появится готовый контент и UX подгрузки роликов

  const [activeTab, setActiveTab] = useState<'color' | 'video' | 'image'>('color');
  const [selectedColor, setSelectedColor] = useState<string>(initialColor || '#000000');
  const [selectedMedia, setSelectedMedia] = useState<
    { type: 'video'; item: IVideoList } | { type: 'image'; item: IBackgroundImageList } | null
  >(null);

  const videos = useMemo(() => backgroundOptions?.data ?? [], [backgroundOptions]);
  const images = useMemo(() => backgroundImages?.data ?? [], [backgroundImages]);
  const defaultImage = forest;

  useEffect(() => {
    if (!isShow) {
      return;
    }

    if (videoTabEnabled && initialType === 'video') {
      setActiveTab('video');
    } else if (initialType === 'image') {
      setActiveTab('image');
    } else {
      setActiveTab('color');
    }
    setSelectedColor(initialColor || '#000000');

    if (initialType === 'video') {
      const currentVideo = videos.find((video) => video.video === initialValue);
      setSelectedMedia(currentVideo ? { type: 'video', item: currentVideo } : null);
    } else if (initialType === 'image') {
      const currentImage = images.find((image) => image.image === initialValue);
      setSelectedMedia(currentImage ? { type: 'image', item: currentImage } : null);
    } else {
      setSelectedMedia(null);
    }
  }, [isShow, initialType, initialColor, initialValue, images, videoTabEnabled, videos]);

  const handleApply = () => {
    if (activeTab === 'color') {
      onApply({ type: 'none', color: selectedColor });
      return;
    }

    if (activeTab === 'video' && selectedMedia?.type === 'video') {
      onApply({ type: 'video', item: selectedMedia.item });
      return;
    }

    if (activeTab === 'image' && selectedMedia?.type === 'image') {
      onApply({ type: 'image', item: selectedMedia.item });
    }
  };

  const renderColorPicker = () => (
    <div className="flex flex-col items-center gap-4 py-6">
      <ColorInput
        label="Выберите цвет фона"
        value={selectedColor}
        onChange={setSelectedColor}
        className="h-32 w-40 items-center justify-center rounded-card"
      >
        <span
          className="block h-24 w-36 rounded-card border border-ink-subtle shadow-[inset_0_2px_8px_rgba(0,0,0,0.12)]"
          style={{ backgroundColor: selectedColor }}
        />
      </ColorInput>
      <div className="text-ui-xs uppercase tracking-[0.08em] text-ink-muted">{selectedColor.toUpperCase()}</div>
    </div>
  );

  const renderVideos = () => {
    if (isVideosLoading) {
      return <div className="py-8 text-center text-ui-xs text-ink-muted">Загружаем список видео…</div>;
    }

    if (!videos.length) {
      return <div className="py-8 text-center text-ui-xs text-ink-muted">Нет доступных видео.</div>;
    }

    return (
      <div className="grid max-h-[45vh] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
        {videos.map((video) => {
          const selected = selectedMedia?.type === 'video' && selectedMedia.item.id === video.id;

          return (
            <Pressable
              key={video.id}
              aria-pressed={selected}
              onClick={() => setSelectedMedia({ type: 'video', item: video })}
              className={classNames(
                'flex w-full items-start gap-3 rounded-card p-3 text-left transition-colors',
                selected ? 'bg-primary text-white' : 'bg-ink-muted/40 hover:bg-ink-subtle',
              )}
            >
              <img
                src={makeAssetUrl(video.thumbnail)}
                alt={video.title}
                className="h-20 w-20 flex-shrink-0 rounded-control object-cover"
                onError={(event) => {
                  event.currentTarget.src = defaultImage;
                }}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className={classNames('text-ui-strong', selected ? 'text-white' : 'text-ink')}>
                  {video.title}
                </span>
                <span className={classNames('text-ui-xs line-clamp-2', selected ? 'text-white/80' : 'text-ink-muted')}>
                  {video.description?.trim() || 'Без описания'}
                </span>
              </div>
            </Pressable>
          );
        })}
      </div>
    );
  };

  const renderImages = () => {
    if (isImagesLoading) {
      return <div className="py-8 text-center text-ui-xs text-ink-muted">Загружаем изображения…</div>;
    }

    if (!images.length) {
      return <div className="py-8 text-center text-ui-xs text-ink-muted">Нет доступных изображений.</div>;
    }

    return (
      <div className="grid max-h-[45vh] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
        {images.map((image) => {
          const selected = selectedMedia?.type === 'image' && selectedMedia.item.id === image.id;

          return (
            <Pressable
              key={image.id}
              aria-pressed={selected}
              onClick={() => setSelectedMedia({ type: 'image', item: image })}
              className={classNames(
                'flex w-full items-start gap-3 rounded-card p-3 text-left transition-colors',
                selected ? 'bg-primary text-white' : 'bg-ink-muted/40 hover:bg-ink-subtle',
              )}
            >
              <img
                src={makeAssetUrl(image.image)}
                alt={image.title}
                className="h-20 w-20 flex-shrink-0 rounded-control object-cover"
                onError={(event) => {
                  event.currentTarget.src = defaultImage;
                }}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className={classNames('text-ui-strong line-clamp-1', selected ? 'text-white' : 'text-ink')}>
                  {image.title}
                </span>
                <span className={classNames('text-ui-xs line-clamp-2', selected ? 'text-white/80' : 'text-ink-muted')}>
                  {image.description?.trim() || 'Без описания'}
                </span>
              </div>
            </Pressable>
          );
        })}
      </div>
    );
  };

  const allTabs: Array<{ key: 'color' | 'video' | 'image'; label: string }> = [
    { key: 'color', label: 'Цвет' },
    // TODO(analytics-team): вернуть вкладку «Видео», когда готов контент и UX подгрузки роликов
    { key: 'video', label: 'Видео' },
    { key: 'image', label: 'Фото' },
  ];

  const tabs = videoTabEnabled ? allTabs : allTabs.filter((tab) => tab.key !== 'video');

  const isApplyDisabled =
    (videoTabEnabled && activeTab === 'video' && selectedMedia?.type !== 'video') ||
    (activeTab === 'image' && selectedMedia?.type !== 'image');

  return (
    <Modal
      isOpen={isShow}
      onClose={onClose}
      size="lg"
      title="Выберите фон"
      description="Настройте фон демонстрации: можно выбрать однотонный цвет, видео или статичное изображение."
      footer={
        <>
          <Button size="md" variant="neutral" onClick={onClose}>
            Отмена
          </Button>
          <Button size="md" variant="primary" onClick={handleApply} disabled={isApplyDisabled}>
            Ок
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'dark' : 'neutral'}
              size="md"
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="min-h-[200px]">
          {activeTab === 'color' && renderColorPicker()}
          {videoTabEnabled && activeTab === 'video' && renderVideos()}
          {activeTab === 'image' && renderImages()}
        </div>
      </div>
    </Modal>
  );
};

export default BackgroundSettingsModal;
